import {
    ApiErrorResponse,
    ApiErrorResponseFromJSON,
    ApiValidationErrorResponse,
    ApiValidationErrorResponseFromJSON,
} from '@enter/core';

export async function handleApiError(err: any): Promise<void> {
    // Fetch abort error
    if (err.code === 20) {
        return;
    }
    // fetch cancel error
    if (err instanceof TypeError && err.message === 'Failed to fetch') {
        return;
    }

    const errorMessage = await getErrorMessage(err);
    showError(errorMessage.error, errorMessage.message);
}

export async function getErrorMessage(
    err: Error,
    includeSystemErrors?: boolean,
): Promise<{ error: string; message: string }> {
    if (includeSystemErrors && err instanceof Error) {
        return { error: '', message: err.message };
    }

    if (!(err instanceof Response)) {
        if (typeof err.message === 'string') {
            return { error: '', message: err.message };
        }

        // TODO: Add handling not api errors
        return { error: 'Unrecognized error', message: JSON.stringify(err) };
    }

    if (err.status === 400) {
        const error: ApiValidationErrorResponse = await err.json();
        return handleValidationError(ApiValidationErrorResponseFromJSON(error));
    }

    if (err.status === 403) {
        const error: ApiValidationErrorResponse = await err.json();
        return handleAccessDeniedError(ApiValidationErrorResponseFromJSON(error));
    }

    if (err.status === 504) {
        return handleServerError(
            {
                message:
                    'We added your request to the queue, it will be processed shortly. You can close this page and check the result later.',
            },
            '',
        );
    }

    if (err.status >= 500 && err.status < 600) {
        const error: ApiErrorResponse = await err.json();
        return handleServerError(ApiErrorResponseFromJSON(error));
    }

    return handleUnknownError(await err.json());
}

function handleValidationError(err: ApiValidationErrorResponse) {
    const errors: string[] = [];
    if (err.errors) {
        for (const key of Object.keys(err.errors)) {
            errors.push(`${key}: ${err.errors[key]}`);
        }
    }

    return { error: errors.join('\n'), message: `Trace Id: ${err.traceId}` };
}

function handleAccessDeniedError(err: any) {
    return {
        error: 'The current user does not have permission to view this resource.',
        message: JSON.stringify(err),
    };
}

function handleServerError(err: ApiErrorResponse, customMessage?: string) {
    const errors: string[] = [];
    if (err.message) {
        errors.push(err.message);
    }
    return { error: errors.join('\n'), message: customMessage ?? `Trace Id: ${err.traceId}` };
}

function handleUnknownError(err: any) {
    // TODO: Find a way to handle such errors
    return { error: 'Unknown error', message: JSON.stringify(err) };
}

// TODO: change to better error view
function showError(error: string, message: string): void {
    window.prompt(error, message);
}

export class UnreachableError extends Error {
    constructor(val: never) {
        super(`Unreachable case: ${val}`);
    }
}
